import * as moment from 'moment';
import {
  Component,
  OnInit,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ContentChild,
  TemplateRef,
  OnDestroy,
  ElementRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { ColumnDefinitionFormatType as FormatType } from '@feature/common/enums/column-definition-format-type';
import {Table, TableColumnReorderEvent, TableFilterEvent} from 'primeng/table';
import { IColumnDefinition } from '@common/models/column-definition';
import { ITable, IResizedColumn } from '@common/models/table';
import { ColumnFormatterService } from '@common/services/column-formatter.service';
import { ListWidgetTableColumn, PrimeColumn } from '@common/models/prime-column'
import { DeviceDetectorService } from '@common/device-detection/services/device-detector.service';
import { Currency } from '@currency/models/currency.model';
import { TablePaginatorComponent } from '@common/components/table-paginator/component/table-paginator.component';
import { SortEvent } from 'primeng/api';
import { ExportCsvService } from '@feature/_app_modules/shared/export-csv/services/export-csv-data.service';
import { MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';

@Component({
  selector: 'mojo-list-widget-table',
  templateUrl: './list-widget-table.component.html',
  styleUrls: ['./list-widget-table.component.scss']
})
export class ListWidgetTableComponent
  implements OnInit, OnDestroy, OnChanges {

  alive = true;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly columnFormatterService: ColumnFormatterService,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly exportCsvService: ExportCsvService) {
  }

  headerTouched(event: TouchEvent) {
    event.stopPropagation();
  }
  @ViewChild('dt', { static: true }) dataTable: Table;
  @ViewChild('container', { static: true }) dataTableContainerElement: ElementRef;
  @ViewChild('paginator') paginator: TablePaginatorComponent;
  @ContentChild('rowTemplate', { static: true }) rowTemplateRef: TemplateRef<any>;
  @ContentChild('footerTemplate', { static: true }) footerTemplateRef: TemplateRef<any>;
  @ContentChild('colGroupTemplate', { static: true }) colGroupTemplateRef: TemplateRef<any>;
  @ContentChild('expandedRowTemplate', { static: true }) expandedRowTemplateRef: TemplateRef<any>;

  @Input() rowCountOverride: number;
  @Input() _table: ITable;
  @Input() customSort = false;
  @Output() onCustomSort = new EventEmitter<SortEvent>();

  @Input() set table(value: ITable) {

    this._table = value;
    if (this.paginator != undefined && this.paginator) {
      this.paginator.goToFirstPage();
    }
  }

  get table(): ITable {

    return this._table;

  }

  @Input() availableColumns: IColumnDefinition[];
  @Input() loading = false;
  @Input() showTitle = true;
  @Input() rows = 7;
  @Input() tableTitle = '';
  @Input() displayExportButton = true;
  @Input() displayEditColumnsButton = true;
  @Input() resizableColumns = true;
  @Input() reorderableColumns = true;
  @Input() currentCurrency: Currency = null;
  @Input() showPaginator = true;
  @Input() frozenColumnNames: string[] = [];
  @Input() dataKey: string; // needed for expanded rows
  expandedRowKeys: { [p: string]: boolean } = {}; // object whose keys are the data key values for expanded rows

  @Output() onRowSelect = new EventEmitter<any>();
  @Output() onColReorder = new EventEmitter<any>();
  @Output() selectedColumnsChanged = new EventEmitter<IColumnDefinition[]>();
  @Output() onRowClick = new EventEmitter<any>();

  /**
   * @deprecated  please use  selectedColumnsChanged
   * */
  @Output() dialogSaved = this.selectedColumnsChanged;
  @Output() resetColumns = new EventEmitter();
  @Output() onColResize = new EventEmitter<IResizedColumn>();
  @Output() onDownloadButtonClicked = new EventEmitter<any>();

  displayDialog: boolean;
  displayContent = false;
  selectedColumns: IColumnDefinition[];
  primeColumns: PrimeColumn[];
  totalRecords: number;
  selectedRow: any;

  @ViewChild('exportMenu') exportMenu: ContextMenu;
  exportMenuItems: MenuItem[];

  isIsoDate(str) {
    return (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(str));
  }

  exportFunction = (evt) => {
    if (this.isIsoDate(evt.data)) {
      const d = (moment(new Date(evt.data)).format('DD/MM/YYYY'));
      return d;
    };
    return evt.data;
  }
  afterFiltered (event: TableFilterEvent){
    if (!this.showPaginator)
      this.totalRecords = event.filteredValue.length;
  }
  getPrimeColumns() {
    if (this.table && this.table.Columns) {
      return this.table.Columns.filter(c=> !c.IsSystemColumn).map(x => {
        const column = {
          field: x.Name,
          width: x.Width,
          isSystemColumn: x.IsSystemColumn,
          header: x.Header,
          type: x.Type,
          isFrozen: false,
          exportable: !x.IsSystemColumn,
          sortable: x.Sortable,
        };
        return column;
      });
    }

    return [];
  }


  private cachedColumns: IColumnDefinition[];

  exportAllColumns(): void {
    if (!this.availableColumns) {
      return;
    }

    const exportableColumns = this.availableColumns
      .filter(x => !x.IsSystemColumn)
      .map(x => ({
        FieldName: x.Field,
        Label: x.Header,
        Type: x.FormatType
      }));

    this.exportCsvService.export(this.table.Rows.map(row => {
      const newRow = {};
      exportableColumns.forEach(col => {
        newRow[col.FieldName] = row[col.FieldName] || row['System' + col.FieldName];
      });
      return newRow;
    }), exportableColumns);
  }


  exportVisibleColumns(): void {
    const exportableColumns = this.primeColumns
      .filter(c => !c.isSystemColumn)
      .map(x => {
        return { FieldName: x.field, Label: x.header, Type: x.type }
      });
    this.exportCsvService.export(this.table.Rows, exportableColumns);
  }

  showEditDialog() {
    this.cachedColumns = this.selectedColumns;
    this.displayDialog = true;
  }

  reset() {
    this.resetColumns.emit();
    this.displayDialog = false;
  }

  save() {
    this.selectedColumnsChanged.emit(this.selectedColumns);
    this.displayDialog = false;
  }

  cancel() {
    this.selectedColumns = this.cachedColumns;
    this.displayDialog = false;
  }


  ngOnInit() {
    this.setSelectionMode();
    this.initializeExportMenu();
  }

  private handleTouchEvent = (evt: Event) => true;

  ngAfterViewInit() {
    setTimeout(() => {
      this.displayContent = true;
      this.changeDetector.detectChanges();
    }, 150);

    setTimeout(() => {
      const el = document.getElementsByTagName('th');
      for (let _i = 0; _i < el.length; _i++) {
        const e = el[_i];
        e.addEventListener('touchstart', this.handleTouchEvent, false);
        e.addEventListener('touchend', this.handleTouchEvent, false);
        e.addEventListener('touchcancel', this.handleTouchEvent, false);
        e.addEventListener('touchmove', this.handleTouchEvent, false);
      }
    }, 3000);

  }

  ngOnChanges(changes: SimpleChanges): void {
    this.primeColumns = this.getPrimeColumns();
    this.matchSelectedColumnsFromTable();
  }

  private matchSelectedColumnsFromTable() {
    if (!this.availableColumns || !this.table) {
      return;
    }


    const nonSystemCols = this.table.Columns.filter(c => c.IsSystemColumn === false);
    const matched = this.availableColumns.filter(c => nonSystemCols.find(col => col.Name === c.Field));

    matched.forEach(x => {
      const col = this.table.Columns.filter(y => y.Name === x.Field)[0];
      if (col != null) {
        x.Width = col.Width;
      }
    });

    this.selectedColumns = matched;
    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {

  }

  getColSize(size) {
    return size + 'px';
  }
  rowSelected(event) {
    this.onRowSelect.emit(event);
  }

  setSelectionMode() {
    if (this.onRowSelect && this.onRowSelect.observers.length > 0) {
      // setting the properties below causes the data table
      // to display the pointer cursor when hovering on a row
      this.dataTable.selectionMode = 'single';
      this.dataTable.rowHover = false;
    }
  }

  columnsReordered(event: TableColumnReorderEvent) {
    const order: string[] = event.columns.map(x => x.field);
    this.onColReorder.emit(order);
  }

  columnsResized(event) {

    const oldAbsSize: number = event.element.offsetWidth - event.delta;
    const percentageChange: number = (event.delta / oldAbsSize);

    const column = this.table.Columns.filter(x => !x.IsSystemColumn && x.Header === event.element.innerText.trim())[0];
    this.onColResize.emit({ FieldName: column.Name, WidthChange: percentageChange } as IResizedColumn);
  }

  prepareColumnName(col: ListWidgetTableColumn): string {
    if (col.type === FormatType.Currency && this.currentCurrency != undefined) {
      return `${col.header} (${this.currentCurrency.Symbol})`;
    }
    else {
      return col.header;
    }

  }

  isColumnRightAlign(columnType: number): boolean {
    const result = (columnType === FormatType.Currency ||
      columnType === FormatType.Numeric0Dp ||
      columnType === FormatType.Numeric2Dp);
    return result;
  }

  formatByColumnType(rowData, columnType: number): string {

    if (this.currentCurrency && columnType === FormatType.Currency) {
      // deliberately override formatting for this col
      return this.columnFormatterService.formatByType(rowData, FormatType.Numeric2Dp);
    }
    return this.columnFormatterService.formatByType(rowData, columnType);
  }

  isMobile(): boolean {
    return this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
  }

  rowClick(rowData: any) {
    this.onRowClick.emit(rowData);
  }

  handleCustomSort(data:SortEvent){
    this.onCustomSort.emit(data);
  }

  handleSort(data:SortEvent){
    this.setTotalRowLast();
  }

  setTotalRowLast() {
    if (!this.table.hasTotalRow) return;
    if (!this.dataTable?.value?.length) return;
    const totalRowIndex = this.dataTable.value.findIndex(x => x.isTotalRow);
    if (totalRowIndex === -1 || totalRowIndex === this.dataTable.value.length - 1) return;
    const totalRow = this.dataTable.value[totalRowIndex];
    this.dataTable.value.splice(totalRowIndex, 1);
    this.dataTable.value.push(totalRow);
  }

  private initializeExportMenu() {
    this.exportMenuItems = [
      {
        label: 'Visible Columns',
        icon: 'fa-fw fa fa-download',
        style: {
          'font-size': '14px',
          'white-space': 'nowrap',
          'display': 'block'
        },
        command: () => this.exportVisibleColumns()
      },
      {
        label: 'All Columns',
        icon: 'fa-fw fa fa-download',
        style: {
          'font-size': '14px',
          'white-space': 'nowrap',
          'display': 'block'
        },
        command: () => this.exportAllColumns()
      }
    ];
  }

  showExportMenu(event: MouseEvent) {
    if (event.button === 2) {
      this.exportMenu.show(event);
      event.preventDefault();
    } else if (event.button === 0) {
      this.exportVisibleColumns();
    }
  }
}
